import React from "react";
import AppModal from "../AppModal/AppModal";
import { PostApiMethod, GetApiMethod } from "../../core/services/apiService";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector, useDispatch } from "react-redux";
import { showToaster } from "../../core/redux/toastSlice";
import CountryCodeDropdown from "./CountryCodeDropdown";
import MonumentTenantPicker from "./MonumentTenantPicker";
import {
    MonumentUnitGroup,
    MonumentLeadSource,
    MonumentTenant,
    removeCountryCode,
    validatePhoneNumber,
    validateMonumentNewLead,
    monumentNewLeadCanAutoSubmit,
    getMonumentTargets,
    hasMonumentMatch,
    getMonumentTarget,
    buildMonumentUnitLabel,
    buildMonumentNewLeadPayload,
    buildMonumentExistingPayload,
} from "./monumentPayloads";


interface VoiceCallCustomerDetailsProps {
    isOpen: boolean;
    onClose: (cancelledDuringCall?: boolean) => void;
    callSid?: string;
    existingCustomerDetails?: any[];
    locationId?: string;
    // Monument support: the location's PMS provider and the caller's number, threaded
    // from the inbound notification so the modal can branch and pre-fill the lead phone.
    pms_provider?: string;
    contact_from?: string;
    countdown?: number;
}

export interface VoiceCallCustomerDetailsRef {
    autoSubmit: () => Promise<void>;
    getIsSubmitted: () => boolean;
    getIsCancelledDuringCall: () => boolean;
    resetCancelledDuringCall: () => void;
}

// Utility Functions
// (Monument payload builders, phone helpers, and validation now live in
//  ./monumentPayloads so the voice and video modals cannot drift.)

/**
 * Validates new customer form data
 */
const validateNewCustomer = (
    tenantDetails: any,
    dateNeeded: Date | null,
    isAutoSubmit: boolean
): { [key: string]: string } => {
    const newErrors: { [key: string]: string } = {};

    if (!isAutoSubmit) {
        if (!tenantDetails.firstName.trim()) newErrors.firstName = "First Name is required";
        if (!tenantDetails.lastName.trim()) newErrors.lastName = "Last Name is required";
        if (!dateNeeded) newErrors.dateNeeded = "Date Needed is required";
        if (!tenantDetails.advSource) newErrors.advSource = "Advertising Source is required";
    }

    return newErrors;
};

/**
 * Determines classification defaults for existing customers
 * Uses priority: Inquiry > Reservation > Rental
 */
const determineClassificationDefaults = (
    classification: string,
    customer: any | undefined
) => {
    let finalClassification = classification;
    let finalInquiryNo = "";
    let finalReservationNumber = "";
    let finalLeaseNumber = "";

    // If no classification selected, use priority: Inquiry > Reservation > Rental
    if (!finalClassification) {
        if (customer?.Inquiries?.length > 0) {
            finalClassification = "Inquiry";
        } else if (customer?.Reservations?.length > 0) {
            finalClassification = "Reservation";
        } else if (customer?.Rentals?.length > 0) {
            finalClassification = "Rental";
        }
    }

    // Apply default first record logic for each classification
    if (finalClassification === "Inquiry") {
        if (customer?.Inquiries?.length > 0) {
            finalInquiryNo = customer.Inquiries[0].InquiryNumber;
        }
    } else if (finalClassification === "Reservation") {
        if (customer?.Reservations?.length > 0) {
            finalReservationNumber = customer.Reservations[0].ReservationNumber;
        }
    } else if (finalClassification === "Rental") {
        if (customer?.Rentals?.length > 0) {
            finalLeaseNumber = customer.Rentals[0].LeaseNumber;
        }
    }

    return { finalClassification, finalInquiryNo, finalReservationNumber, finalLeaseNumber };
};

/**
 * Builds payload for new customer submission
 */
const buildNewCustomerPayload = (
    tenantDetails: any,
    dateNeeded: Date | null,
    callSid: string | undefined,
    locationId: string | undefined,
    facilityId: any,
    agentName: string,
    customerFirstName: string,
    customerLastName: string
) => {
    return {
        tenantId: tenantDetails.tenantId.trim() || 0,
        firstName: tenantDetails.firstName.trim(),
        lastName: tenantDetails.lastName.trim(),
        facilityId: facilityId,
        countryId: tenantDetails.countryId || 1,
        dateNeeded: dateNeeded ? dateNeeded.toISOString().split("T")[0] : tenantDetails.dateNeeded,
        advSource: tenantDetails.advSource || "Voice Call",
        customerType: "new",
        callType: "voice",
        callSid: callSid,
        locationId: locationId,
        agentName: agentName
    };
};

/**
 * Builds payload for existing customer submission
 */
const buildExistingCustomerPayload = (
    tenantDetails: any,
    customer: any | undefined,
    callSid: string | undefined,
    locationId: string | undefined,
    facilityId: any,
    classificationDefaults: {
        finalClassification: string;
        finalInquiryNo: string;
        finalReservationNumber: string;
        finalLeaseNumber: string;
    },
    agentName: string,
    customerFirstName: string,
    customerLastName: string
) => {
    const { finalClassification, finalInquiryNo, finalReservationNumber, finalLeaseNumber } = classificationDefaults;

    // Common fields for all classifications
    const basePayload = {
        classification: finalClassification,
        customerType: "existing",
        callType: "voice",
        locationId: locationId,
        callSid: callSid,
        facilityId: facilityId,
        advSource: "Voice Call",
        agentName: agentName,
        customerFirstName: customerFirstName,
        customerLastName: customerLastName
    };

    // Build payload based on final classification
    if (finalClassification === "Inquiry") {
        return {
            facilityId: facilityId,
            InquiryNo: tenantDetails.inquiryDetails || finalInquiryNo,
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    } else if (finalClassification === "Reservation") {
        return {
            facilityId: facilityId,
            ReservationNumber: tenantDetails.reservationDetails || finalReservationNumber,
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    } else if (finalClassification === "Rental") {
        // Find the selected rental to get its TenantId (not the top-level customer TenantID)
        const selectedLeaseNumber = tenantDetails.rentalDetails || finalLeaseNumber;
        const selectedRental = customer?.Rentals?.find((rental: any) => rental.LeaseNumber === selectedLeaseNumber);

        return {
            TenantId: selectedRental?.TenantId?.toString() || customer?.TenantID?.toString() || "",
            LeaseNumber: selectedLeaseNumber,
            facilityId: facilityId,
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    } else {
        return {
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            facilityId: facilityId,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    }
};

/**
 * Submits customer details to API
 */
const submitCustomerDetails = async (payload: any): Promise<{ success: boolean; error?: string }> => {
    try {
        console.log(payload, 'Customer Payload (voice) ==============>');
        const res = await PostApiMethod("tenant/sendCallSummary/postCustomerDetails", payload);
        console.log("Customer API Response:", res);
        return { success: true };
    } catch (err) {
        console.error("Customer API Error:", err);
        return {
            success: false,
            error: err instanceof Error ? err.message : "Failed to submit customer details. Please try again."
        };
    }
};

// Empty State Component
interface EmptyStateProps {
    type: 'inquiry' | 'reservation' | 'rental';
    onSwitchToNew?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ type, onSwitchToNew }) => {
    const emptyStateConfig = {
        inquiry: {
            icon: 'bi-inbox',
            title: 'No Inquiry Records',
            message: 'This customer doesn\'t have any inquiry records.',
        },
        reservation: {
            icon: 'bi-calendar-x',
            title: 'No Reservation Records',
            message: 'This customer doesn\'t have any active reservations.',
        },
        rental: {
            icon: 'bi-house-x',
            title: 'No Rental Records',
            message: 'This customer doesn\'t have any active rental agreements.',
        }
    };

    const config = emptyStateConfig[type];

    return (
        <div className="text-center py-3">
            <div className="mb-2">
                <i className={`bi ${config.icon} text-muted`} style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
            </div>
            <h6 className="text-muted mb-1" style={{ fontSize: '0.95rem' }}>{config.title}</h6>
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                {config.message}
            </p>
        </div>
    );
};

// Reusable Record Card Component
interface RecordCardProps {
    record: any;
    type: 'inquiry' | 'reservation' | 'rental';
    isSelected: boolean;
    onSelect: () => void;
}

const RecordCard: React.FC<RecordCardProps> = ({ record, type, isSelected, onSelect }) => {
    const cardConfig = {
        inquiry: {
            icon: 'bi-question-circle-fill',
            borderColor: 'border-info',
            bgColor: 'bg-info',
            badgeBg: isSelected ? 'bg-info text-white' : 'bg-secondary',
            checkBg: 'bg-info',
            label: 'Inquiry',
            number: record.InquiryNumber || 'Inquiry',
        },
        reservation: {
            icon: 'bi-calendar-check-fill',
            borderColor: 'border-warning',
            bgColor: 'bg-warning',
            badgeBg: isSelected ? 'bg-warning text-dark' : 'bg-secondary',
            checkBg: 'bg-warning',
            label: 'Reservation',
            number: record.ReservationNumber,
        },
        rental: {
            icon: 'bi-house-fill',
            borderColor: 'border-success',
            bgColor: 'bg-success',
            badgeBg: isSelected ? 'bg-success text-white' : 'bg-secondary',
            checkBg: 'bg-success',
            label: 'Rental',
            number: record.LeaseNumber || `REN`,
        }
    };

    const config = cardConfig[type];

    return (
        <div className="col-md-6 col-lg-3">
            <div
                className={`card h-100 ${isSelected ? `border-2 ${config.borderColor} ${config.bgColor} bg-opacity-15 shadow` : 'border border-secondary'}`}
                style={{
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease-in-out',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                }}
                onClick={onSelect}
                onMouseEnter={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                    }
                }}
            >
                {isSelected && (
                    <div className="position-absolute top-0 end-0 p-1">
                        <i className={`bi bi-check-circle-fill text-white ${config.checkBg} rounded-circle`} style={{ fontSize: '1.1rem' }}></i>
                    </div>
                )}
                <div className="card-body p-2 text-center">
                    <div className="mb-1">
                        <i className={`bi ${config.icon} ${isSelected ? 'text-white' : 'text-secondary'}`} style={{ fontSize: '1.5rem' }}></i>
                    </div>
                    <h6 className={`card-title mb-1 ${isSelected ? 'text-dark fw-bold' : 'text-dark'}`} style={{ fontSize: '0.85rem' }}>
                        {config.number}
                    </h6>
                    <p className={`card-text mb-1 ${isSelected ? 'text-dark' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>
                        <i className="bi bi-geo-alt me-1"></i>
                        {record.UnitNumber || 'N/A'}
                    </p>
                    <span className={`badge ${config.badgeBg}`} style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>
                        {config.label}
                    </span>
                </div>
            </div>
        </div>
    );
};

const VoiceCallCustomerDetails = React.forwardRef<VoiceCallCustomerDetailsRef, VoiceCallCustomerDetailsProps>((
    { isOpen, onClose, callSid, existingCustomerDetails, locationId, pms_provider, contact_from, countdown = 0 },
    ref
) => {
    const dispatch = useDispatch();
    const LoginUserDetails = useSelector((state: any) => state.auth.users);
    const isMonument = pms_provider === "monument";
    const [customerType, setCustomerType] = React.useState<"new" | "existing">("new");
    // Monument new-lead: the required unit-group dropdown, fetched server-side (the
    // frontend has no facilityUuid — the backend resolves it from customer_contact).
    const [unitGroupUuid, setUnitGroupUuid] = React.useState("");
    const [unitGroups, setUnitGroups] = React.useState<MonumentUnitGroup[]>([]);
    // Whether the unit-group fetch has completed. Distinguishes "still loading" from
    // "loaded, but this facility has none", so the dropdown doesn't show "Loading…" forever.
    const [unitGroupsLoaded, setUnitGroupsLoaded] = React.useState(false);
    // Monument new-lead: the optional lead-source attribution the agent picks (Phone /
    // Website / Walk-In / …). Options come from the same server-side fetch as unit groups.
    const [leadSourceUuid, setLeadSourceUuid] = React.useState("");
    const [leadSources, setLeadSources] = React.useState<MonumentLeadSource[]>([]);
    // Monument new-lead address (docs mark these required; the live runtime does not).
    const [addressLine1, setAddressLine1] = React.useState("");
    const [addressLine2, setAddressLine2] = React.useState("");
    const [tenantDetails, setTenantDetails] = React.useState({
        tenantId: "",
        firstName: "",
        lastName: "",
        countryId: "1",
        facilityId: "",
        dateNeeded: "",
        advSource: "",
        classification: "",
        inquiryDetails: "",
        reservationDetails: "",
        rentalDetails: "",
    });
    const [dateNeeded, setDateNeeded] = React.useState<Date | null>(null);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [showNoDataMessage, setShowNoDataMessage] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [apiError, setApiError] = React.useState<string>("");
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [isFetchingByPhone, setIsFetchingByPhone] = React.useState(false);
    const [isCancelledDuringCall, setIsCancelledDuringCall] = React.useState(false);
    // Monument new-lead contact: a dedicated phone (pre-filled from the caller's number) plus
    // an optional email. Monument's create-lead needs a phone OR an email; a voice call always
    // has the caller's number, so pre-fill it — the agent can override it or add an email.
    // (Kept separate from phoneNumber above, which is the existing-customer phone search.)
    const [leadPhone, setLeadPhone] = React.useState("");
    const [email, setEmail] = React.useState("");
    React.useEffect(() => {
        setLeadPhone(contact_from ? removeCountryCode(contact_from) : "");
        setEmail("");
    }, [contact_from, callSid]);

    // FIX Issue #6: Reset isSubmitted when callSid changes (new call)
    React.useEffect(() => {
        setIsSubmitted(false);
        setApiError("");
    }, [callSid]);
    const [fetchedCustomerByPhone, setFetchedCustomerByPhone] = React.useState<any>(null);
    const [showPhoneNotFoundMessage, setShowPhoneNotFoundMessage] = React.useState(false);

    // Store combined customer data (combines all records into one)
    const [combinedCustomerData, setCombinedCustomerData] = React.useState<any>(null);

    // Monument existing-tenant selection. A caller (incoming number or a manual phone search)
    // can match more than one distinct Monument account; the write-back note is account-level,
    // so the agent can pick which account it lands on (defaulting to the first). These hold the
    // distinct tenants and the chosen account id, kept separate from the SSM record-selection
    // state. Populated from the raw (un-merged) records on open and on phone search. See
    // getMonumentTenants.
    const [monumentTenants, setMonumentTenants] = React.useState<MonumentTenant[]>([]);
    // Prospects that matched, shown as their own picker section. A note picked here goes
    // to the LEAD, which is what stops the summary creating a second lead for someone
    // Monument already has.
    const [monumentLeads, setMonumentLeads] = React.useState<MonumentTenant[]>([]);
    const [selectedMonumentAccountUuid, setSelectedMonumentAccountUuid] = React.useState("");
    // Which unit the call was about, for a tenancy holding several. "" means the agent
    // did not narrow it and the note names every unit instead of guessing one.
    const [selectedMonumentUnitLabel, setSelectedMonumentUnitLabel] = React.useState("");
    // The tenant the write-back note attaches to. Defaults to the FIRST matched account when
    // none is explicitly picked (any count), so the flow never blocks and the note always has
    // a target; the picker (shown for >1 account, with the first pre-selected) lets the agent
    // override this default to the correct account.
    // Tenancies before prospects: the fallback below is what the call-end auto-submit uses
    // when the agent picked nothing, and an existing tenancy should outrank an enquiry.
    const monumentMatches: MonumentTenant[] = [...monumentTenants, ...monumentLeads];
    const selectedMonumentTenant: MonumentTenant | null =
        monumentMatches.find((t) => t.accountUuid === selectedMonumentAccountUuid) ||
        monumentMatches[0] || null;
    // Whether the record the note will attach to is a prospect rather than a tenancy.
    // Drives the wording of the "Existing ..." banner; false for SSM and Hummingbird.
    const isMonumentLeadSelected = isMonument && selectedMonumentTenant?.targetType === "lead";

    // Monument: load the new-lead form options (unit groups + lead sources) for the
    // dropdowns. The backend resolves the facility from the location and returns empty
    // lists for non-Monument locations.
    React.useEffect(() => {
        if (!isMonument || !locationId) { setUnitGroups([]); setLeadSources([]); setUnitGroupsLoaded(false); return; }
        let cancelled = false;
        setUnitGroupsLoaded(false);
        (async () => {
            try {
                const res: any = await GetApiMethod(`tenant/sendCallSummary/monumentUnitGroups?locationId=${locationId}`);
                if (cancelled) return;
                setUnitGroups(Array.isArray(res?.data?.data) ? res.data.data : []);
                setLeadSources(Array.isArray(res?.data?.leadSources) ? res.data.leadSources : []);
            } catch (err) {
                if (!cancelled) { setUnitGroups([]); setLeadSources([]); }
            } finally {
                if (!cancelled) setUnitGroupsLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [isMonument, locationId]);

    // FIX Issue #6: Reset isSubmitted when callSid changes (new call)
    React.useEffect(() => {
        setIsSubmitted(false);
        setApiError("");
        setCombinedCustomerData(null); // Reset combined data
        setIsCancelledDuringCall(false); // Reset cancelled flag for new call
    }, [callSid]);

    // Initialize when modal opens
    React.useEffect(() => {
        if (isOpen) {
            const defaultDate = new Date();
            defaultDate.setDate(defaultDate.getDate() + 10);
            setDateNeeded(defaultDate);

            // For voice calls with existing customer data
            if (existingCustomerDetails && existingCustomerDetails.length > 0) {
                setCustomerType("existing");

                // SSM: merge all records into record[0]. Safe ONLY because the SSM write-back
                // keys on globally-unique per-record numbers (InquiryNo / ReservationNumber /
                // LeaseNumber) the agent selects, not on record identity. Monument's note is
                // account-level, so it must NOT be merged this way — it uses monumentTenants
                // (getMonumentTenants) to keep distinct accounts selectable.
                const allInquiries = existingCustomerDetails.flatMap((record: any) => record.Inquiries || []);
                const allReservations = existingCustomerDetails.flatMap((record: any) => record.Reservations || []);
                const allRentals = existingCustomerDetails.flatMap((record: any) => record.Rentals || []);

                // Use first record for basic customer info
                const firstCustomer = existingCustomerDetails[0];

                // Create combined customer object with ALL data from ALL records
                const combined = {
                    ...firstCustomer,
                    Inquiries: allInquiries,
                    Reservations: allReservations,
                    Rentals: allRentals
                };
                setCombinedCustomerData(combined);

                // Check if combined data has any records.
                //
                // Monument is asked a different question. Its records carry no Inquiries or
                // Reservations, and a PROSPECT has an empty Rentals by definition — so the
                // SSM test below calls every lead an empty result and renders "No Customer
                // Data Found → Continue as New Customer" over a caller Monument already has,
                // which is how a duplicate lead gets created. For Monument, "we found
                // something" means a write target came back.
                const hasInquiries = allInquiries.length > 0;
                const hasReservations = allReservations.length > 0;
                const hasRentals = allRentals.length > 0;
                setShowNoDataMessage(isMonument
                    ? !hasMonumentMatch(existingCustomerDetails)
                    : (!hasInquiries && !hasReservations && !hasRentals));

                setTenantDetails({
                    tenantId: firstCustomer.TenantID?.toString() || "",
                    firstName: firstCustomer.FirstName || "",
                    lastName: firstCustomer.LastName || "",
                    countryId: firstCustomer.CountryId?.toString() || "1",
                    facilityId: "",
                    dateNeeded: defaultDate.toISOString().split("T")[0],
                    advSource: "",
                    classification: "",
                    inquiryDetails: "",
                    reservationDetails: "",
                    rentalDetails: "",
                });
            } else {
                // For voice calls without existing data
                setCustomerType("new");
                setTenantDetails({
                    tenantId: "",
                    firstName: "",
                    lastName: "",
                    countryId: "1",
                    facilityId: "",
                    dateNeeded: defaultDate.toISOString().split("T")[0],
                    advSource: "",
                    classification: "",
                    inquiryDetails: "",
                    reservationDetails: "",
                    rentalDetails: "",
                });
            }
            // Reset states
            setIsSubmitted(false);
            setIsSubmitting(false);
            setApiError("");
            setErrors({});
            setPhoneNumber("");
            setUnitGroupUuid("");
            setLeadSourceUuid("");
            setAddressLine1("");
            setAddressLine2("");
            setIsFetchingByPhone(false);
            setFetchedCustomerByPhone(null);
            setShowPhoneNotFoundMessage(false);
            // Monument: derive the distinct tenants from the incoming call's raw records (one
            // per account) so the agent can pick which account the note attaches to. Empty for
            // SSM / no-data. Pre-select the first account (highlighted in the picker, drives the
            // note by default); the agent can override to the correct account.
            const opened = isMonument && existingCustomerDetails
                ? getMonumentTargets(existingCustomerDetails)
                : { tenants: [], leads: [] };
            setMonumentTenants(opened.tenants);
            setMonumentLeads(opened.leads);
            setSelectedMonumentAccountUuid((opened.tenants[0] || opened.leads[0])?.accountUuid || "");
            setSelectedMonumentUnitLabel("");
            // Don't reset isCancelledDuringCall here - it needs to persist when popup reopens after call ends
        }
    }, [isOpen, existingCustomerDetails]);

    const handleDateChange = (date: Date | null) => {
        setDateNeeded(date);
        setTenantDetails({
            ...tenantDetails,
            dateNeeded: date ? date.toISOString().split("T")[0] : ""
        });
    };

    const handlePhoneNumberChange = (value: string) => {
        setPhoneNumber(value);
        // Clear error when user starts typing
        if (errors.phoneNumber) {
            setErrors({ ...errors, phoneNumber: "" });
        }
        // Clear "not found" message when user modifies the number
        if (showPhoneNotFoundMessage) {
            setShowPhoneNotFoundMessage(false);
        }
    };

    const handleFetchCustomerByPhone = async () => {
        // Validate phone number
        const validation = validatePhoneNumber(phoneNumber);
        if (!validation.isValid) {
            setErrors({ ...errors, phoneNumber: validation.error });
            return;
        }

        setErrors({});
        setIsFetchingByPhone(true);
        setShowPhoneNotFoundMessage(false);
        try {
            const fetchPayload = {
                phoneNumber: removeCountryCode(phoneNumber.trim()),
                locationId: locationId,
                action: "fetch"
            };
            const res: any = await PostApiMethod("tenant/sendCallSummary/fetchCustomerDetailsByPhone", fetchPayload);
            console.log("Fetch by Phone API Response:", res);

            const apiData = res.data.data || [];

            if (apiData?.length > 0) {
                // Combine all Inquiries, Reservations, and Rentals from ALL records
                const allInquiries = apiData?.flatMap((record: any) => record.Inquiries || []);
                const allReservations = apiData?.flatMap((record: any) => record.Reservations || []);
                const allRentals = apiData?.flatMap((record: any) => record.Rentals || []);

                // Use first record for basic customer info (name, ID, etc)
                const firstCustomer: any = apiData.length > 0 ? apiData[0] : [];

                // Create a combined customer object with ALL data from ALL records
                const combinedCustomer = {
                    ...firstCustomer,
                    Inquiries: allInquiries,
                    Reservations: allReservations,
                    Rentals: allRentals
                };

                setFetchedCustomerByPhone(combinedCustomer);

                // Check if combined data has any records
                const hasInquiries = allInquiries?.length > 0;
                const hasReservations = allReservations?.length > 0;
                const hasRentals = allRentals?.length > 0;
                // Same reasoning as the open effect, and here it is not cosmetic: falling into
                // the !hasData branch switches the modal to "new customer" and tells the agent
                // to proceed as one. For a caller who matched only a lead that means creating a
                // second lead for someone Monument already has — the exact duplicate this flow
                // exists to prevent.
                const hasData = isMonument
                    ? hasMonumentMatch(apiData)
                    : (hasInquiries || hasReservations || hasRentals);

                // If customer found but has NO data (no inquiries, reservations, or rentals)
                // Keep them in "new" customer mode with pre-filled info
                if (!hasData) {
                    setCustomerType("new");
                    setShowNoDataMessage(false);

                    // Pre-fill form with customer info
                    setTenantDetails({
                        tenantId: firstCustomer?.TenantID?.toString() || "",
                        firstName: firstCustomer?.FirstName || "",
                        lastName: firstCustomer?.LastName || "",
                        countryId: firstCustomer?.CountryId?.toString() || "1",
                        facilityId: "",
                        dateNeeded: dateNeeded ? dateNeeded.toISOString().split("T")[0] : "",
                        advSource: "",
                        classification: "",
                        inquiryDetails: "",
                        reservationDetails: "",
                        rentalDetails: "",
                    });

                    // Show info message that customer has no data
                    setApiError("");
                    dispatch(
                        showToaster({
                            showDialog: true,
                            dialogBgColor: "bg-secondary",
                            dialogMsg: "Customer found but has no rental, reservation, or inquiry history. Please proceed as a new customer with the pre-filled details.",
                            timer: '5000'
                        })
                    );
                } else {
                    // Customer has data - switch to existing customer mode
                    setCustomerType("existing");
                    setShowNoDataMessage(false);

                    // Clear combinedCustomerData so the display uses fetchedCustomerByPhone instead
                    setCombinedCustomerData(null);

                    // Monument: a phone search can surface a different set of accounts than the
                    // incoming call — rebuild the distinct tenants from the raw search results
                    // (not the merged record) and pre-select the first (the agent can override).
                    if (isMonument) {
                        const found = getMonumentTargets(apiData);
                        setMonumentTenants(found.tenants);
                        setMonumentLeads(found.leads);
                        setSelectedMonumentAccountUuid((found.tenants[0] || found.leads[0])?.accountUuid || "");
                        setSelectedMonumentUnitLabel("");
                    }

                    setTenantDetails({
                        tenantId: firstCustomer.TenantID?.toString() || "",
                        firstName: firstCustomer.FirstName || "",
                        lastName: firstCustomer.LastName || "",
                        countryId: firstCustomer.CountryId?.toString() || "1",
                        facilityId: "",
                        dateNeeded: dateNeeded ? dateNeeded.toISOString().split("T")[0] : "",
                        advSource: "",
                        classification: "",
                        inquiryDetails: "",
                        reservationDetails: "",
                        rentalDetails: "",
                    });
                    setApiError("");
                }
            } else {
                // No customer found - search field remains visible for another attempt
                setFetchedCustomerByPhone(null);
                setShowPhoneNotFoundMessage(true);
                setApiError("");
            }
        } catch (err) {
            console.error("Fetch by Phone API Error:", err);
            setApiError("Failed to fetch customer details. Please try again.");
            setFetchedCustomerByPhone(null);
            setShowPhoneNotFoundMessage(false);
        } finally {
            setIsFetchingByPhone(false);
        }
    };

    const handlePrimaryButtonClick = () => {
        const formElement = document.getElementById("voice-customer-details-form") as HTMLFormElement;
        if (formElement) {
            formElement.requestSubmit();
        }
    };

    // Handle cancel button: Submit immediately if countdown is active (after call ends)
    const handleCancelClick = async () => {
        // During call (countdown = 0): Just close popup and mark as cancelled
        if (countdown === 0) {
            setIsCancelledDuringCall(true); // Track that user cancelled during call
            onClose(true); // Pass true to indicate cancellation during call
            return;
        }

        // After call ends (countdown > 0): Submit immediately then close
        // Double-submit prevention: Check if already submitted or submitting
        if (isSubmitted || isSubmitting) {
            onClose(false);
            return;
        }

        // Submit immediately
        setIsSubmitting(true);
        try {
            await handleTenantDetailsSubmit(undefined, true);
            // isSubmitted will be set to true in handleTenantDetailsSubmit on success
        } catch (error) {
            console.error("Submit on cancel failed:", error);
            // Still close even if submit fails
        } finally {
            setIsSubmitting(false);
            onClose(false);
        }
    };

    const handleTenantDetailsSubmit = async (e?: React.FormEvent, isAutoSubmit: boolean = false) => {
        if (e) e.preventDefault();

        // Prevent duplicate submissions
        if (isSubmitted || isSubmitting) return;

        // Clear previous errors
        setErrors({});
        setApiError("");

        // Get facilityId from combined customer data or phone search results
        const getfacilityId = () => {
            // Use combined customer data from voice call
            if (combinedCustomerData?.facilityId) {
                return combinedCustomerData.facilityId;
            }
            // Use phone search result
            if (fetchedCustomerByPhone?.facilityId) {
                return fetchedCustomerByPhone.facilityId;
            }
            return null;
        };

        // Monument writes leads/notes, not SSM records — its payload is a different,
        // smaller shape and skips the SSM classification/date/advSource machinery.
        if (isMonument) {
            // Existing-tenant write-back targets the SELECTED Monument account (its own
            // AccountUuid), not a merged record[0]. selectedMonumentTenant defaults to the
            // first matched account (agent-overridable via the picker); it is only null when
            // the read returned no writable account, which the AccountUuid guard below catches.
            const customer = selectedMonumentTenant?.record;
            const agentName = LoginUserDetails?.name || "";
            // The new-lead phone field is pre-filled from the caller's number (see the reset
            // effect), so read it directly. Do NOT fall back to contact_from at submit time: that
            // would re-introduce the caller id after the agent deliberately clears the field for
            // an email-only lead, and a partial/garbled caller id (<10 digits) would then block
            // the submit with an unclearable phone error on a field the agent already emptied.
            // Validate what the agent TYPED, submit what Monument wants: removeCountryCode
            // strips every non-digit, so validating its output let "wqewe" pass as email-only.
            const leadPhoneRaw = leadPhone?.trim() || "";
            const phone = removeCountryCode(leadPhoneRaw);
            // Auto-submit has no UI to report an error, so an unparseable phone must SKIP the
            // write: removeCountryCode collapses "wqewe" to "", which the gate reads as no phone.
            const leadPhoneInvalid = !!leadPhoneRaw && !validatePhoneNumber(leadPhoneRaw).isValid;
            let payload: any;
            if (customerType === "new") {
                if (!isAutoSubmit) {
                    // An empty phone is fine — Monument accepts phone OR email, so an email-only
                    // lead validates once the agent clears the pre-filled phone.
                    const newErrors = validateMonumentNewLead(
                        { firstName: tenantDetails.firstName, lastName: tenantDetails.lastName, phone: leadPhoneRaw, email }
                    );
                    // The Monument lead phone owns errors.leadPhone; errors.phoneNumber belongs to
                    // the existing-customer phone SEARCH field, which renders at the same time.
                    if (newErrors.phoneNumber) { newErrors.leadPhone = newErrors.phoneNumber; delete newErrors.phoneNumber; }
                    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
                } else if (leadPhoneInvalid || !monumentNewLeadCanAutoSubmit({ firstName: tenantDetails.firstName, lastName: tenantDetails.lastName, phone, email })) {
                    // B2: an auto-submit missing a Monument-required field would POST a
                    // payload the backend silently refuses (no lead, no note). Skip the
                    // write instead of reporting a false success — leave isSubmitted false.
                    console.warn("[Monument] voice auto-submit skipped: new-lead is missing a required field (name / phone-or-email) or the phone entered is not a valid number.");
                    return;
                }
                payload = buildMonumentNewLeadPayload({
                    tenantDetails,
                    unitGroupUuid,
                    phone,
                    email,
                    callSid,
                    locationId,
                    agentName,
                    callType: "voice",
                    leadSourceUuid,
                    addressLine1: addressLine1.trim(),
                    addressLine2: addressLine2.trim(),
                });
            } else {
                // B1: without a Monument AccountUuid the server-side write-back router
                // matches neither branch and the note is silently dropped. Refuse to
                // submit rather than report a false success.
                // customer defaults to the first matched account, so this only fires when the
                // read returned NO writable Monument account (no AccountUuid at all) — nothing
                // to attach the note to.
                // A prospect has no AccountUuid by design, so the guard asks whether the
                // selection resolves to ANY write target — checking AccountUuid alone would
                // reject every lead and push the agent into creating a duplicate.
                if (!getMonumentTarget(customer).targetUuid) {
                    if (!isAutoSubmit) {
                        setApiError("No Monument account is selected for this customer. Search and select the tenant before submitting.");
                    } else {
                        console.warn("[Monument] voice auto-submit skipped: existing customer has no Monument AccountUuid.");
                    }
                    return;
                }
                payload = buildMonumentExistingPayload(customer, callSid, locationId, agentName, "voice", buildMonumentUnitLabel(selectedMonumentTenant, selectedMonumentUnitLabel));
            }
            setIsSubmitting(true);
            const result = await submitCustomerDetails(payload);
            setIsSubmitting(false);
            if (result.success) {
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } else {
                setApiError(result.error || "Failed to submit customer details");
            }
            return;
        }

        if (customerType === "new") {
            // Validate new customer
            const validationErrors = validateNewCustomer(tenantDetails, dateNeeded, isAutoSubmit);
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }

            // Get customer names from form data
            const customerFirstName = tenantDetails.firstName.trim();
            const customerLastName = tenantDetails.lastName.trim();

            // Build new customer payload
            const newCustomerPayload = buildNewCustomerPayload(
                tenantDetails,
                dateNeeded,
                callSid,
                locationId,
                getfacilityId(),
                LoginUserDetails?.name || "",
                customerFirstName,
                customerLastName
            );

            // Submit new customer
            setIsSubmitting(true);
            const result = await submitCustomerDetails(newCustomerPayload);
            setIsSubmitting(false);

            if (result.success) {
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } else {
                setApiError(result.error || "Failed to submit customer details");
            }
        } else {
            // Existing customer - Voice call (from combined customer data or phone search)
            const customer = combinedCustomerData || fetchedCustomerByPhone;

            // Determine classification defaults using business logic
            const classificationDefaults = determineClassificationDefaults(
                tenantDetails.classification,
                customer
            );

            // Get customer names from form or fetched data
            const customerFirstName = tenantDetails.firstName.trim() || customer?.FirstName || "";
            const customerLastName = tenantDetails.lastName.trim() || customer?.LastName || "";

            // Build existing customer payload
            const existingCustomerPayload = buildExistingCustomerPayload(
                tenantDetails,
                customer,
                callSid,
                locationId,
                getfacilityId(),
                classificationDefaults,
                LoginUserDetails?.name || "",
                customerFirstName,
                customerLastName
            );

            // Submit existing customer
            setIsSubmitting(true);
            const result = await submitCustomerDetails(existingCustomerPayload);
            setIsSubmitting(false);

            if (result.success) {
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose(false);
            } else {
                setApiError(result.error || "Failed to submit customer details");
            }
        }
    };

    // Expose autoSubmit function and isSubmitted state via ref
    React.useImperativeHandle(ref, () => ({
        autoSubmit: async () => {
            if (!isSubmitted) {
                await handleTenantDetailsSubmit(undefined, true);
            }
        },
        getIsSubmitted: () => isSubmitted,
        getIsCancelledDuringCall: () => isCancelledDuringCall,
        resetCancelledDuringCall: () => setIsCancelledDuringCall(false)
    }));

    if (!isOpen) return null;

    return (
        <AppModal
            title="Customer Details"
            bodyText=""
            primaryButtonLabel={isSubmitting ? "Submitting..." : "Submit"}
            secondaryButtonLabel="Cancel"
            primaryButtonEventEmitter={handlePrimaryButtonClick}
            secondaryButtonEventEmitter={handleCancelClick}
            stayAppear={true}
            disablePrimaryButton={showNoDataMessage || isSubmitting}
        >
            <form id="voice-customer-details-form" onSubmit={handleTenantDetailsSubmit} style={{ position: 'relative' }}>
                {/* Countdown Timer Message */}
                {countdown > 0 && (
                    <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert" style={{ borderLeft: '4px solid #ff9800' }}>
                        <div className="d-flex align-items-center">
                            <i className="bi bi-clock-fill me-2" style={{ fontSize: '1.5rem' }}></i>
                            <div>
                                <strong>Auto-submitting in {countdown} second{countdown !== 1 ? 's' : ''}...</strong>
                                <br />
                                <small className="text-muted">Fill in the details or submit now to avoid auto-submission</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* API Error Message */}
                {apiError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>Error:</strong> {apiError}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setApiError("")}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {/* Elegant Loading Overlay - For Submission */}
                {isSubmitting && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 1050,
                            backdropFilter: 'blur(4px)',
                            borderRadius: '0.5rem'
                        }}
                    >
                        <div className="text-center">
                            <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem', borderWidth: '0.3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-3 text-white fw-medium">Processing...</div>
                        </div>
                    </div>
                )}

                {/* Loading Overlay - For Phone Fetch */}
                {isFetchingByPhone && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 1050,
                            backdropFilter: 'blur(4px)',
                            borderRadius: '0.5rem'
                        }}
                    >
                        <div className="text-center">
                            <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem', borderWidth: '0.3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-3 text-white fw-medium">
                                <i className="bi bi-telephone-fill me-2"></i>
                                Fetching Customer Details...
                            </div>
                        </div>
                    </div>
                )}

                {/* Voice call existing customer info (from voice call or phone search) */}
                {customerType === "existing" && (combinedCustomerData || fetchedCustomerByPhone) && (
                    <>
                        {/* Phone Number Search Field for Existing Customers - Always visible */}
                        <div className="row mb-3">
                            <div className="col-9">
                                <label className="form-label fw-semibold bg-light px-2 py-1 rounded">
                                    <i className="bi bi-telephone text-primary me-1"></i>
                                    Search by Phone Number
                                </label>
                                <input
                                    type="tel"
                                    name="phoneNumber"
                                    className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                    value={phoneNumber}
                                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                                    placeholder="Enter phone number Ex: 9034675791"
                                    disabled={isFetchingByPhone}
                                />
                                {errors.phoneNumber && <small className="text-danger d-block mt-1">{errors.phoneNumber}</small>}
                            </div>
                            <div className="col-3 text-center" style={{ paddingTop: "40px" }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary w-100"
                                    onClick={handleFetchCustomerByPhone}
                                    disabled={isFetchingByPhone}
                                >
                                    {isFetchingByPhone ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Fetching...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-search me-1"></i>
                                            Fetch
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Phone Not Found Message for Existing Customers */}
                        {showPhoneNotFoundMessage && (
                            <div className="row mb-3">
                                <div className="text-center col-12">
                                    <div className="alert alert-warning d-flex align-items-center py-2 mb-0" role="alert">
                                        <div className="d-flex align-items-center">
                                            <small className="mb-0">
                                                <strong> <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '1.2rem' }}></i> Customer not found.</strong>
                                                <br />
                                                Please try a different number.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* OR Divider */}
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="d-flex align-items-center">
                                    <hr className="flex-grow-1" style={{ borderTop: '2px solid #dee2e6' }} />
                                    <span className="px-3 text-muted fw-bold" style={{ fontSize: '0.9rem' }}>OR</span>
                                    <hr className="flex-grow-1" style={{ borderTop: '2px solid #dee2e6' }} />
                                </div>
                            </div>
                        </div>

                        {/* Existing Customer Header - Below Phone Search */}
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="alert alert-success border-success mb-0 py-2" style={{ borderLeft: '3px solid #198754' }}>
                                    <div className="d-flex align-items-center">
                                        {/* A prospect is not a customer: they have never rented, never
                                            paid, and hold no unit. This banner is the largest identity
                                            text on the screen and sets how the agent opens the call —
                                            servicing an account reads very differently from selling one.
                                            Saying "Customer" here also contradicts the "Lead" wording in
                                            the note below, and the bold green line is the one an agent
                                            believes. "Existing" still distinguishes this from the New
                                            Customer mode, so only the noun changes.
                                            Monument-only: RecordType is never set by SSM or Hummingbird,
                                            so their banner is untouched. */}
                                        <i
                                            className={`bi ${isMonumentLeadSelected ? 'bi-person-plus-fill' : 'bi-person-check-fill'} text-success me-2`}
                                            style={{ fontSize: '1.25rem' }}
                                        ></i>
                                        <div>
                                            <small className="mb-0 fw-bold text-success d-block">
                                                {isMonumentLeadSelected ? "Existing Lead" : "Existing Customer"} {fetchedCustomerByPhone && "(Found by Phone)"}
                                            </small>
                                            <small className="mb-0 text-dark">
                                                <strong>
                                                    {isMonument && selectedMonumentTenant
                                                        // Monument: the merged record[0] name can belong to a
                                                        // DIFFERENT account than the one the note targets, so show
                                                        // the SELECTED tenant (defaults to the first matched account,
                                                        // overridable via the picker) rather than the merged name.
                                                        ? `${selectedMonumentTenant.firstName} ${selectedMonumentTenant.lastName}`.trim()
                                                        : <>{fetchedCustomerByPhone?.FirstName || combinedCustomerData?.FirstName || existingCustomerDetails?.[0]?.FirstName} {fetchedCustomerByPhone?.LastName || combinedCustomerData?.LastName || existingCustomerDetails?.[0]?.LastName}</>}
                                                </strong>
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* New customer indicator with phone search */}
                {customerType === "new" && (
                    <>
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="alert alert-primary border-primary mb-0 py-2" style={{ borderLeft: '3px solid #0d6efd' }}>
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-person-plus-fill text-primary me-2" style={{ fontSize: '1.25rem' }}></i>
                                        <div>
                                            <small className="mb-0 fw-bold text-primary d-block">New Customer</small>
                                            <small className="mb-0 text-muted">Search by phone number or fill in the details below</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Phone Number Search Field - Always visible */}
                        <div className="row mb-3">
                            <div className="col-9">
                                <label className="form-label fw-semibold bg-light px-2 py-1 rounded">
                                    <i className="bi bi-telephone text-primary me-1"></i>
                                    Search by Phone Number
                                </label>
                                <input
                                    type="tel"
                                    name="phoneNumber"
                                    className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                    value={phoneNumber}
                                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                                    placeholder="Enter phone number Ex: 9034675791"
                                    disabled={isFetchingByPhone}
                                />
                                {errors.phoneNumber && <small className="text-danger d-block mt-1">{errors.phoneNumber}</small>}
                            </div>
                            <div className="col-3 text-center" style={{ paddingTop: "40px" }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary w-100"
                                    onClick={handleFetchCustomerByPhone}
                                    disabled={isFetchingByPhone}
                                >
                                    {isFetchingByPhone ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Fetching...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-search me-1"></i>
                                            Fetch
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Phone Not Found Message */}
                        {showPhoneNotFoundMessage && customerType === "new" && (
                            <div className="row mb-3">
                                <div className="text-center col-12">
                                    <div className="alert alert-warning d-flex align-items-center py-2 mb-0" role="alert">

                                        <div className="d-flex align-items-center">
                                            <small className="mb-0">
                                                <strong> <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '1.2rem' }}></i> Customer not found.</strong>
                                                <br />
                                                Please try a different number or fill the details below to proceed as a new customer.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* OR Divider - Always visible for new customers */}
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="d-flex align-items-center">
                                    <hr className="flex-grow-1" style={{ borderTop: '2px solid #dee2e6' }} />
                                    <span className="px-3 text-muted fw-bold" style={{ fontSize: '0.9rem' }}>OR</span>
                                    <hr className="flex-grow-1" style={{ borderTop: '2px solid #dee2e6' }} />
                                </div>
                            </div>
                        </div>

                        {/* New Customer Form Section Label - Always visible */}
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="bg-light border rounded py-2 px-3">
                                    <small className="text-muted fw-semibold">
                                        <i className="bi bi-file-text me-2"></i>
                                        Enter Customer Information
                                    </small>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* No data message for existing customers */}
                {customerType === "existing" && showNoDataMessage && (
                    <div className="row mb-3 justify-content-center">
                        <div className="col-lg-10">
                            <div className="card border-warning" style={{ borderWidth: '2px' }}>
                                <div className="card-body text-center p-3">
                                    <div className="mb-2">
                                        <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '2.5rem' }}></i>
                                    </div>
                                    <h6 className="card-title text-warning fw-bold mb-2">No Customer Data Found</h6>
                                    <p className="card-text text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                                        This customer has no rental, reservation, or inquiry history in the system.
                                    </p>
                                    <small className="text-muted d-block mb-3" style={{ fontSize: '0.85rem' }}>
                                        <i className="bi bi-arrow-right-circle me-1"></i>
                                        You can proceed with the new customer process using their existing details.
                                    </small>
                                    <button
                                        type="button"
                                        className="btn btn-primary px-3"
                                        style={{ transition: 'all 0.2s ease-in-out' }}
                                        onClick={() => {
                                            setCustomerType("new");
                                            setShowNoDataMessage(false);
                                            setFetchedCustomerByPhone(null);
                                            // Pre-populate TenantID from existing customer details or phone search
                                            const customer = existingCustomerDetails?.[0] || fetchedCustomerByPhone;
                                            if (customer?.TenantID) {
                                                setTenantDetails({
                                                    ...tenantDetails,
                                                    tenantId: customer.TenantID.toString()
                                                });
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.03)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    >
                                        <i className="bi bi-person-plus-fill me-2"></i>
                                        Continue as New Customer
                                    </button>
                                    <div className="mt-2">
                                        <small className="badge bg-light text-dark border">
                                            <i className="bi bi-check-circle-fill text-success me-1"></i>
                                            Tenant ID will be auto-filled
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Show all fields for new customers */}
                {customerType === "new" && (
                    <div className="mb-3">
                        {/* Tenant ID & Country ID — SSM only */}
                        {!isMonument && (
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="tenantId"
                                        id="tenantId"
                                        className={`form-control ${errors.tenantId ? 'is-invalid' : ''}`}
                                        value={tenantDetails.tenantId}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, tenantId: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Tenant ID"
                                    />
                                    <label htmlFor="tenantId">
                                        <i className="bi bi-person-badge text-primary me-2"></i>
                                        Tenant ID
                                    </label>
                                    {errors.tenantId && <div className="invalid-feedback">{errors.tenantId}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <CountryCodeDropdown
                                    value={tenantDetails.countryId}
                                    onChange={(countryId) => setTenantDetails({ ...tenantDetails, countryId: countryId.toString() })}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        )}

                        {/* First & Last Name */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="firstName"
                                        id="firstName"
                                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.firstName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, firstName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="First Name"
                                    />
                                    <label htmlFor="firstName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        First Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="lastName"
                                        id="lastName"
                                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.lastName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, lastName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Last Name"
                                    />
                                    <label htmlFor="lastName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        Last Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Date Needed & Advertising Source — SSM only */}
                        {!isMonument && (
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label htmlFor="dateNeeded" className="form-label text-muted small mb-2">
                                    <i className="bi bi-calendar-event text-primary me-1"></i>
                                    Date Needed <span className="text-danger">*</span>
                                </label>
                                <DatePicker
                                    id="dateNeeded"
                                    selected={dateNeeded}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className={`form-control ${errors.dateNeeded ? 'is-invalid' : ''}`}
                                    placeholderText="Select date needed"
                                    minDate={new Date()}
                                    disabled={isSubmitting}
                                />
                                {errors.dateNeeded && <div className="invalid-feedback d-block">{errors.dateNeeded}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="advSource" className="form-label text-muted small mb-2">
                                    <i className="bi bi-megaphone text-primary me-1"></i>
                                    Advertising Source <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.advSource ? 'is-invalid' : ''}`}
                                    id="advSource"
                                    name="advSource"
                                    value={tenantDetails.advSource}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, advSource: e.target.value })}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select...</option>
                                    <option value="Phone">Phone</option>
                                    <option value="Website">Website</option>
                                    <option value="Kiosk">Kiosk</option>
                                    <option value="QR code">QR Code</option>
                                </select>
                                {errors.advSource && <div className="invalid-feedback">{errors.advSource}</div>}
                            </div>
                        </div>
                        )}

                        {/* Monument new-lead fields — one floating-label style throughout,
                            matching the First/Last Name inputs above. */}
                        {isMonument && (
                            <>
                                {/* Address — optional. Monument's docs mark these required; its API accepts a lead without them. */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                id="addressLine1"
                                                className="form-control"
                                                value={addressLine1}
                                                onChange={(e) => setAddressLine1(e.target.value)}
                                                placeholder="Street address"
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="addressLine1">
                                                <i className="bi bi-geo-alt text-primary me-2"></i>
                                                Address Line 1
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                id="addressLine2"
                                                className="form-control"
                                                value={addressLine2}
                                                onChange={(e) => setAddressLine2(e.target.value)}
                                                placeholder="Apt, suite, unit, etc."
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="addressLine2">
                                                <i className="bi bi-geo-alt text-primary me-2"></i>
                                                Address Line 2
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Phone OR email — Monument's create-lead needs at least one
                                    (phone is pre-filled from the caller's number). */}
                                <div className="row g-3 mb-1">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="tel"
                                                id="monumentPhone"
                                                name="monumentPhone"
                                                className={`form-control ${errors.leadPhone ? 'is-invalid' : ''}`}
                                                value={leadPhone}
                                                onChange={(e) => {
                                                    setLeadPhone(e.target.value);
                                                    if (errors.leadPhone || errors.contact) setErrors({ ...errors, leadPhone: "", contact: "" });
                                                }}
                                                placeholder="Ex: 9034675791"
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="monumentPhone">
                                                <i className="bi bi-telephone text-primary me-2"></i>
                                                Phone Number
                                            </label>
                                            {errors.leadPhone && <div className="invalid-feedback d-block">{errors.leadPhone}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                type="email"
                                                id="monumentEmail"
                                                name="monumentEmail"
                                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    if (errors.email || errors.contact) setErrors({ ...errors, email: "", contact: "" });
                                                }}
                                                placeholder="name@example.com"
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor="monumentEmail">
                                                <i className="bi bi-envelope text-primary me-2"></i>
                                                Email
                                            </label>
                                            {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    {errors.contact
                                        ? <small className="text-danger">{errors.contact}</small>
                                        : <small className="text-muted">Enter a phone number or an email.</small>}
                                </div>

                                {/* Unit type and lead source — both optional. */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <select
                                                className="form-select"
                                                id="unitGroupUuid"
                                                value={unitGroupUuid}
                                                onChange={(e) => setUnitGroupUuid(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select a unit type…</option>
                                                {unitGroups.map((g) => (
                                                    <option key={g.unitGroupUuid} value={g.unitGroupUuid}>{g.label}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="unitGroupUuid">
                                                <i className="bi bi-box-seam text-primary me-2"></i>
                                                Unit Type
                                            </label>
                                        </div>
                                        {!unitGroupsLoaded && (
                                            <div className="form-text text-muted">Loading unit types…</div>
                                        )}
                                        {unitGroupsLoaded && unitGroups.length === 0 && (
                                            <div className="form-text text-muted">No unit types are available for this facility — you can still create the lead. Ask an admin to check the setup.</div>
                                        )}
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <select
                                                className="form-select"
                                                id="leadSourceUuid"
                                                value={leadSourceUuid}
                                                onChange={(e) => setLeadSourceUuid(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select a lead source…</option>
                                                {leadSources.map((s) => (
                                                    <option key={s.leadSourceUuid} value={s.leadSourceUuid}>{s.description}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="leadSourceUuid">
                                                <i className="bi bi-megaphone text-primary me-2"></i>
                                                Lead Source
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Monument existing tenant — no SSM classification/records; the note is the
                    auto-generated call summary, so the agent confirms and submits. */}
                {customerType === "existing" && isMonument && (() => {
                    // Exactly what the note will be stamped with, so the agent sees it before submitting.
                    const monumentUnitStamp = buildMonumentUnitLabel(selectedMonumentTenant, selectedMonumentUnitLabel);
                    return (
                        <>
                            <MonumentTenantPicker
                                tenants={monumentTenants}
                                leads={monumentLeads}
                                selectedAccountUuid={selectedMonumentAccountUuid}
                                onSelect={(uuid) => { setSelectedMonumentAccountUuid(uuid); setSelectedMonumentUnitLabel(""); }}
                                selectedUnitLabel={selectedMonumentUnitLabel}
                                onSelectUnit={setSelectedMonumentUnitLabel}
                                disabled={isSubmitting}
                            />
                            <div
                                className="d-flex align-items-start gap-2 px-3 py-2 mb-3 rounded-3"
                                style={{ backgroundColor: '#eef5ff', border: '1px solid #cfe2ff' }}
                                role="note"
                            >
                                <i
                                    className="bi bi-info-circle-fill"
                                    style={{ color: '#0d6efd', fontSize: '1rem', marginTop: '2px', flexShrink: 0 }}
                                ></i>
                                <div style={{ fontSize: '0.8rem', lineHeight: 1.4, color: '#334155' }}>
                                    {selectedMonumentTenant ? (
                                        <>
                                            {selectedMonumentTenant.targetType === "lead"
                                                ? "The call summary will be saved as a note on this lead in Monument."
                                                : monumentUnitStamp
                                                    ? <>{"The call summary will be saved as a note on this tenant's Monument account, headed "}<span className="fw-semibold text-dark">{monumentUnitStamp}</span>{"."}</>
                                                    : "The call summary will be saved as a note on this tenant's Monument account."}
                                        </>
                                    ) : (
                                        "The call summary will be saved as a note in Monument."
                                    )}
                                </div>
                            </div>
                        </>
                    );
                })()}

                {/* Call Purpose - only for existing SSM customers */}
                {customerType === "existing" && !showNoDataMessage && !isMonument && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-2 d-inline-flex align-items-center">
                                <i className="bi bi-telephone text-primary me-2"></i>
                                Call Purpose
                            </label>
                            <select
                                className="form-select form-select-lg"
                                style={{
                                    borderWidth: '2px',
                                    fontSize: '1rem',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                value={tenantDetails.classification}
                                onChange={(e) => setTenantDetails({ ...tenantDetails, classification: e.target.value })}
                                disabled={isSubmitting}
                            >
                                <option value="">-- Select Call Purpose --</option>
                                <option value="Inquiry">📋 Inquiry</option>
                                <option value="Reservation">📅 Reservation</option>
                                <option value="Rental">🏠 Rental</option>
                            </select>
                            {!tenantDetails.classification && (
                                <small className="text-muted mt-1 d-block">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Please select the purpose of this call
                                </small>
                            )}
                        </div>
                    </div>
                )}

                {/* Inquiry Details - Square Cards */}
                {customerType === "existing" && !showNoDataMessage && !isMonument && tenantDetails.classification === "Inquiry" && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                <i className="bi bi-question-circle text-info me-2"></i>
                                Select Inquiry
                            </label>
                            {((combinedCustomerData || fetchedCustomerByPhone)?.Inquiries?.length > 0) ? (
                                <div className="row g-3">
                                    {(combinedCustomerData || fetchedCustomerByPhone)?.Inquiries.map((inquiry: any, index: number) => (
                                        <RecordCard
                                            key={index}
                                            record={inquiry}
                                            type="inquiry"
                                            isSelected={tenantDetails.inquiryDetails === inquiry.InquiryNumber}
                                            onSelect={() => setTenantDetails({
                                                ...tenantDetails,
                                                inquiryDetails: tenantDetails.inquiryDetails === inquiry.InquiryNumber ? "" : inquiry.InquiryNumber
                                            })}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState type="inquiry" />
                            )}
                        </div>
                    </div>
                )}

                {/* Reservation Details - Square Cards */}
                {customerType === "existing" && !showNoDataMessage && !isMonument && tenantDetails.classification === "Reservation" && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                <i className="bi bi-calendar-check text-warning me-2"></i>
                                Select Reservation
                            </label>
                            {((combinedCustomerData || fetchedCustomerByPhone)?.Reservations?.length > 0) ? (
                                <div className="row g-3">
                                    {(combinedCustomerData || fetchedCustomerByPhone)?.Reservations.map((reservation: any, index: number) => (
                                        <RecordCard
                                            key={index}
                                            record={reservation}
                                            type="reservation"
                                            isSelected={tenantDetails.reservationDetails === reservation.ReservationNumber}
                                            onSelect={() => setTenantDetails({
                                                ...tenantDetails,
                                                reservationDetails: tenantDetails.reservationDetails === reservation.ReservationNumber ? "" : reservation.ReservationNumber
                                            })}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState type="reservation" />
                            )}
                        </div>
                    </div>
                )}

                {/* Rental Details - Square Cards */}
                {customerType === "existing" && !showNoDataMessage && !isMonument && tenantDetails.classification === "Rental" && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                <i className="bi bi-house text-success me-2"></i>
                                Select Rental
                            </label>
                            {((combinedCustomerData || fetchedCustomerByPhone)?.Rentals?.length > 0) ? (
                                <div className="row g-3">
                                    {(combinedCustomerData || fetchedCustomerByPhone)?.Rentals.map((rental: any, index: number) => (
                                        <RecordCard
                                            key={index}
                                            record={rental}
                                            type="rental"
                                            isSelected={tenantDetails.rentalDetails === rental.LeaseNumber}
                                            onSelect={() => setTenantDetails({
                                                ...tenantDetails,
                                                rentalDetails: tenantDetails.rentalDetails === rental.LeaseNumber ? "" : rental.LeaseNumber
                                            })}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState type="rental" />
                            )}
                        </div>
                    </div>
                )}
            </form>
        </AppModal>
    );
});

VoiceCallCustomerDetails.displayName = 'VoiceCallCustomerDetails';

export default VoiceCallCustomerDetails;